"use client";

import { useState, useEffect, useRef, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Building2, Mail, ArrowRight, AlertCircle, Loader2, ArrowLeft, RefreshCw, KeyRound } from "lucide-react";

async function parseJsonResponse(res: Response, fallbackMessage: string = "An unexpected error occurred"): Promise<any> {
    const contentType = res.headers.get("content-type") || "";
    let data: any = null;

    if (contentType.includes("application/json")) {
        try {
            data = await res.json();
        } catch {
            data = null;
        }
    }

    if (!data) {
        const text = await res.text().catch(() => "");
        if (!res.ok) {
            throw new Error(`Server returned status ${res.status}. Please try again later.`);
        }
        try {
            data = JSON.parse(text);
        } catch {
            throw new Error(`Server returned an invalid response format (${res.status}).`);
        }
    }

    if (!res.ok) {
        throw new Error(data?.error || fallbackMessage);
    }

    return data;
}

function LoginPageContent() {
    const router = useRouter();
    const searchParams = useSearchParams();

    const [step, setStep] = useState<"email" | "otp">("email");
    const [email, setEmail] = useState("");
    const [otpCode, setOtpCode] = useState<string[]>(Array(8).fill(""));
    const [session, setSession] = useState("");

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [cooldown, setCooldown] = useState(0);
    const [otpExpiryTimer, setOtpExpiryTimer] = useState(300); // 5 minutes
    const [isOtpExpired, setIsOtpExpired] = useState(false);

    const inputRefs = useRef<HTMLInputElement[]>([]);

    // Global error listener to report client-side crashes to server
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const handleError = (event: ErrorEvent) => {
            fetch('/api/client-log', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    context: 'window.onerror',
                    message: event.message,
                    filename: event.filename,
                    lineno: event.lineno,
                    colno: event.colno,
                    error: event.error?.message || event.error?.toString()
                })
            }).catch(() => {});
        };

        const handleRejection = (event: PromiseRejectionEvent) => {
            fetch('/api/client-log', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    context: 'window.onunhandledrejection',
                    reason: event.reason?.message || event.reason?.toString() || 'Unknown rejection reason',
                    stack: event.reason?.stack
                })
            }).catch(() => {});
        };

        window.addEventListener('error', handleError);
        window.addEventListener('unhandledrejection', handleRejection);

        return () => {
            window.removeEventListener('error', handleError);
            window.removeEventListener('unhandledrejection', handleRejection);
        };
    }, []);

    // Show success message if redirected from onboarding or invitation
    useEffect(() => {
        const onboarded = searchParams.get('onboarded');
        const invited = searchParams.get('invited');
        const slug = searchParams.get('slug');
        const invite = searchParams.get('invite');
        const tenantName = searchParams.get('tenantName');

        if (invite === 'true' && tenantName) {
            setSuccessMessage(`You were invited to join ${tenantName}. Sign in to accept.`);
        } else if (onboarded === 'true' && slug) {
            setSuccessMessage(`Workspace created successfully! Please log in to access ${slug}.`);
        } else if (invited === 'true' && slug) {
            setSuccessMessage(`Invitation accepted! Please log in again to access ${slug}.`);
        }
    }, [searchParams]);

    // Prefill email if provided in query parameters
    useEffect(() => {
        const emailParam = searchParams.get("email");
        if (emailParam) {
            setEmail(emailParam);
        }
    }, [searchParams]);

    // Timer for resend OTP cooldown
    useEffect(() => {
        if (cooldown <= 0) return;
        const timer = setTimeout(() => setCooldown(prev => prev - 1), 1000);
        return () => clearTimeout(timer);
    }, [cooldown]);

    // Timer for OTP expiry
    useEffect(() => {
        if (step !== "otp") return;
        if (otpExpiryTimer > 0 && !isOtpExpired) {
            const timer = setTimeout(() => setOtpExpiryTimer(prev => prev - 1), 1000);
            return () => clearTimeout(timer);
        } else if (otpExpiryTimer === 0 && !isOtpExpired) {
            setIsOtpExpired(true);
            setError("OTP has expired. Please request a new code.");
        }
    }, [otpExpiryTimer, isOtpExpired, step]);

    // Focus first input box on transition to OTP step
    useEffect(() => {
        if (step === "otp" && inputRefs.current[0]) {
            setTimeout(() => inputRefs.current[0]?.focus(), 100);
        }
    }, [step]);

    // Format timer display (MM:SS)
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Validate email
    const validateEmail = (emailStr: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(emailStr) && emailStr.includes('@') && emailStr.includes('.');
    };

    /**
     * Phase 1: Send verification email with OTP code
     */
    async function handleSendOtp(e?: React.FormEvent) {
        if (e) e.preventDefault();
        if (!email.trim()) {
            setError("Email is required");
            return;
        }

        if (!validateEmail(email.trim())) {
            setError("Please enter a valid email address (must include @ and domain like .com)");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const res = await fetch("/api/proxy/api/v1/auth/passwordless/send", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: email.trim() }),
            });

            const data = await parseJsonResponse(res, "Failed to deliver verification code.");

            setSession(data.session);
            setStep("otp");
            setCooldown(60); // 1-minute resend protection
            setOtpExpiryTimer(300); // 5-minute expiry
            setIsOtpExpired(false);
            setOtpCode(Array(8).fill(""));
        } catch (err: any) {
            setError(err.message || "Failed to send verification code. Please try again.");
            fetch('/api/client-log', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    context: 'handleSendOtp catch',
                    message: err.message || err.toString(),
                    email: email
                })
            }).catch(() => {});
        } finally {
            setIsLoading(false);
        }
    }

    /**
     * Phase 2: Verify the custom challenge OTP code
     */
    async function handleVerifyOtp(otpValue?: string) {
        const code = otpValue || otpCode.join("");
        if ((code.length !== 6 && code.length !== 8) || !session) {
            setError("Please enter the complete verification code");
            return;
        }

        if (isOtpExpired) {
            setError("OTP has expired. Please request a new code.");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const res = await fetch("/api/proxy/api/v1/auth/passwordless/verify", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: email.trim(), otp: code, session }),
            });

            const data = await parseJsonResponse(res, "Verification code is incorrect.");

            // Write session parameters locally
            const sessionRes = await fetch('/api/auth/session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token: data.token,
                    accessToken: data.accessToken,
                    refreshToken: data.refreshToken
                }),
            });
            if (!sessionRes.ok) throw new Error('Could not establish workspace session');

            // Authenticated! Check profile setup details
            const meRes = await fetch('/api/proxy/api/v1/auth/me', {
                headers: { Authorization: `Bearer ${data.token}` },
            });
            const me = await parseJsonResponse(meRes, "Could not read user profile metadata");

            // Logged in! Respect redirect param if present (takes priority over onboarding)
            const redirectTo = searchParams.get('redirect');
            if (redirectTo && redirectTo.startsWith('/')) {
                router.push(redirectTo);
                router.refresh();
                return;
            }

            // Redirect to onboarding if this user must provision a workspace.
            // Invited members of an existing tenant are accepted first instead.
            if (me.needsOnboarding || !me.slug) {
                const { tryAcceptPendingInvitation, fetchAuthMe } = await import('@/lib/accept-pending-invitation');
                const accepted = await tryAcceptPendingInvitation(data.token);
                if (accepted) {
                    const meAfter = (await fetchAuthMe()) ?? (await fetchAuthMe(data.token));
                    if (meAfter?.slug) {
                        router.push('/dashboard');
                        router.refresh();
                        return;
                    }
                }
                console.log("[DEBUG Login] User needs onboarding. Redirecting to /auth/onboarding");
                router.push('/auth/onboarding');
                router.refresh();
                return;
            }

            router.push(`/dashboard`);
            router.refresh();
        } catch (err: any) {
            setError(err.message || "OTP verification failed. Please try again.");
            setOtpCode(Array(8).fill(""));
            if (inputRefs.current[0]) {
                inputRefs.current[0].focus();
            }
            fetch('/api/client-log', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    context: 'handleVerifyOtp catch',
                    message: err.message || err.toString(),
                    email: email
                })
            }).catch(() => {});
        } finally {
            setIsLoading(false);
        }
    }

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (step === "email") {
            handleSendOtp();
        } else {
            handleVerifyOtp();
        }
    };

    /**
     * Handle single cell input and focus shifts
     */
    function handleCellChange(index: number, val: string) {
        if (val && !/^\d$/.test(val)) return; // Strictly digit input

        if (isOtpExpired) {
            setError("OTP has expired. Please request a new code.");
            return;
        }

        const newOtp = [...otpCode];
        newOtp[index] = val;
        setOtpCode(newOtp);
        setError(null);

        // Move cursor to next input cell
        if (val !== "" && index < 7) {
            inputRefs.current[index + 1]?.focus();
        }

        // Auto-submit when all fields are filled
        if (newOtp.every(digit => digit !== "") && index === 7) {
            handleVerifyOtp(newOtp.join(""));
        }
    }

    /**
     * Shift focus backwards on Backspace event
     */
    function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === "Backspace") {
            if (otpCode[index] === "" && index > 0) {
                const newOtp = [...otpCode];
                newOtp[index - 1] = "";
                setOtpCode(newOtp);
                inputRefs.current[index - 1]?.focus();
            } else {
                const newOtp = [...otpCode];
                newOtp[index] = "";
                setOtpCode(newOtp);
            }
        }
    }

    /**
     * Support 8-digit code pasting
     */
    function handlePaste(e: React.ClipboardEvent) {
        e.preventDefault();
        const text = e.clipboardData.getData("text").trim().slice(0, 8);
        if (/^\d+$/.test(text)) {
            const newOtp = text.split("").concat(Array(8 - text.length).fill(""));
            setOtpCode(newOtp);

            const nextIndex = Math.min(text.length, 7);
            inputRefs.current[nextIndex]?.focus();

            if (text.length === 6 || text.length === 8) {
                handleVerifyOtp(text);
            }
        }
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4 font-sans antialiased">
            <div className="w-full max-w-md">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl mb-4 shadow-lg shadow-blue-500/10">
                        {step === "email" ? (
                            <Building2 className="w-8 h-8 text-white" />
                        ) : (
                            <Mail className="w-8 h-8 text-white" />
                        )}
                    </div>
                    <h1 className="font-sans text-gray-900 text-3xl font-bold tracking-tight mb-2">
                        {step === "email" ? "Welcome Back" : "Verify Your Identity"}
                    </h1>
                    <p className="text-gray-600 text-sm px-4">
                        {step === "email"
                            ? "Sign in to your MEPIfy account"
                            : `We've sent an 8-digit code to ${email}`
                        }
                    </p>
                </div>

                {/* Card Container */}
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
                    {successMessage && (
                        <div className="mb-6 p-4 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" />
                            <span>{successMessage}</span>
                        </div>
                    )}

                    {error && (
                        <div className="mb-6 p-4 text-sm font-medium text-red-600 bg-red-50 border border-red-150 rounded-xl flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 shrink-0 text-red-500" />
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleFormSubmit} className="space-y-6">
                        {step === "email" ? (
                            <>
                                {/* Email Field */}
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1.5 font-semibold">
                                        Email Address
                                    </label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => {
                                                setEmail(e.target.value);
                                                setError(null);
                                            }}
                                            disabled={isLoading}
                                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all h-11 text-gray-900"
                                            placeholder="your.email@company.com"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Cognito Managed Authentication Notice */}
                                <div className="p-3 bg-blue-50/70 border border-blue-100 rounded-lg text-center">
                                    <p className="text-xs text-blue-800">
                                        We'll send a verification code to your email address. No passwords required.
                                    </p>
                                </div>

                                {/* Continue Button */}
                                <button
                                    type="submit"
                                    disabled={isLoading || !email}
                                    className="w-full h-11 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2 text-sm"
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            <span>Sending Code...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Continue</span>
                                            <ArrowRight className="w-4 h-4" />
                                        </>
                                    )}
                                </button>
                            </>
                        ) : (
                            <>
                                {/* OTP Fields */}
                                <div>
                                    <div className="flex items-center justify-between mb-3">
                                        <label className="text-sm text-gray-700 font-semibold">
                                            Enter verification code
                                        </label>
                                        <div className={`text-xs px-2 py-1 rounded font-semibold ${
                                            otpExpiryTimer < 60
                                                ? 'bg-red-50 text-red-600'
                                                : 'bg-blue-50 text-blue-600'
                                        }`}>
                                            Expires in {formatTime(otpExpiryTimer)}
                                        </div>
                                    </div>

                                    <div className="flex gap-1.5 justify-center" onPaste={handlePaste}>
                                        {otpCode.map((digit, idx) => (
                                            <input
                                                key={idx}
                                                ref={(el) => { if (el) inputRefs.current[idx] = el; }}
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={1}
                                                value={digit}
                                                onChange={(e) => handleCellChange(idx, e.target.value)}
                                                onKeyDown={(e) => handleKeyDown(idx, e)}
                                                className={`w-9 h-12 sm:w-10 sm:h-14 text-center bg-white border ${
                                                    error ? 'border-red-300' : isOtpExpired ? 'border-red-200 bg-red-50' : 'border-gray-200'
                                                } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-lg font-bold text-gray-900 disabled:opacity-50`}
                                                disabled={isLoading || isOtpExpired}
                                            />
                                        ))}
                                    </div>
                                </div>

                                {/* Resend Code */}
                                <div className="text-center">
                                    {cooldown === 0 ? (
                                        <button
                                            type="button"
                                            onClick={() => handleSendOtp()}
                                            disabled={isLoading}
                                            className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center justify-center gap-1.5 mx-auto transition-colors"
                                        >
                                            <RefreshCw className="w-4 h-4" />
                                            Resend Code
                                        </button>
                                    ) : (
                                        <p className="text-sm text-gray-500 font-medium">
                                            Resend code in {cooldown}s
                                        </p>
                                    )}
                                </div>

                                {/* Verify Button */}
                                <button
                                    type="submit"
                                    disabled={isLoading || isOtpExpired || (otpCode.join("").length !== 6 && otpCode.join("").length !== 8)}
                                    className="w-full h-11 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2 text-sm"
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            <span>Verifying...</span>
                                        </>
                                    ) : (
                                        <span>Verify & Sign In</span>
                                    )}
                                </button>
                            </>
                        )}
                    </form>

                    {step === "email" && (
                        <div className="mt-6 text-center">
                            <p className="text-sm text-gray-500 font-medium">
                                Don't have an account?{' '}
                                <a href="/auth/onboarding" className="text-blue-600 hover:text-blue-700 font-semibold hover:underline transition-all">
                                    Create New Account
                                </a>
                            </p>
                        </div>
                    )}

                    {step === "otp" && (
                        /* Back to email */
                        <button
                            onClick={() => {
                                setStep("email");
                                setError(null);
                            }}
                            className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-900 mx-auto transition-colors font-semibold"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back to sign in
                        </button>
                    )}
                </div>

                {/* Support Link */}
                <div className="mt-8 text-center">
                    <p className="text-xs text-gray-500 font-medium">
                        Having trouble signing in?{' '}
                        <a href="/support/contact?source=signin" className="text-blue-600 hover:underline font-semibold">
                            Contact your Product Owner or Administrator
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default function LoginPage() {
    return (
        <Suspense fallback={
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
                <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 p-8 flex flex-col items-center justify-center">
                    <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-3" />
                    <p className="text-sm text-gray-500 font-medium animate-pulse">Loading auth page...</p>
                </div>
            </div>
        }>
            <LoginPageContent />
        </Suspense>
    );
}
